import { useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";

import * as Yup from "yup";
import moment from "moment";
import { Form, Formik } from "formik";
import { useLocation, useNavigate } from "react-router-dom";

import { Box, Button, Typography } from "@mui/material";
import { Stepper, Step, StepLabel } from "@mui/material";
import { styled } from "@mui/material/styles";

import CheckIcon from "@mui/icons-material/Check";
import { StepIconProps } from "@mui/material/StepIcon";

import Toaster from "../../component/Toaster";
import ActionButton from "../../component/ActionButton";
import StepperConnector from "../../component/StepperConnector";
import { CommonUtil, FormValidation, Product } from "../../util/Utils";

import { parentLinkAction } from "../../store/parentLink/parentLinkSlice";

import ParentLinkParentInfo from "./ParentLinkParentInfo";
import ParentLinkStudentInfo from "./ParentLinkStudentInfo";
import ParentLinkRegistrationPayment from "./ParentLinkRegistrationPayment";
import LinkExpire from "../../assets/images/not_valid_url.jpg";

const ColorlibStepIconRoot = styled("div")<{
  ownerState: { completed?: boolean; active?: boolean };
}>(({ theme, ownerState }) => ({
  backgroundColor:
    theme.palette.mode === "dark" ? theme.palette.grey[700] : "#ccc",
  zIndex: 1,
  color: "#fff",
  width: 32,
  height: 24,
  display: "flex",
  borderRadius: "11px",
  justifyContent: "center",
  alignItems: "center",
  ...(ownerState.completed && {
    backgroundColor: Product.ColorCode(),
  }),
  ...(ownerState.active && {
    backgroundColor: Product.ColorCode(),
  }),
}));

function ColorlibStepIcon(props: StepIconProps) {
  const { active, completed, className } = props;

  const icons: { [index: string]: React.ReactElement } = {
    1: <CheckIcon />,
    2: <CheckIcon />,
    3: <CheckIcon />,
    4: <CheckIcon />,
  };

  return (
    <ColorlibStepIconRoot
      ownerState={{ completed, active }}
      className={className}
    >
      {icons[String(props.icon)]}
    </ColorlibStepIconRoot>
  );
}

const ParentLinkStepperContainer = (props: any) => {
  const { t } = useTranslation();
  const location = useLocation();
  const dispatch = useDispatch<any>();

  const queryParams = new URLSearchParams(location.search);
  const detailedId = queryParams.get("detailedId");

  const [schoolOption, setSchoolOption] = useState<any>([]);
  const [countryOption, setCountryOption] = useState<any>([]);
  const [activeStep, setActiveStep] = useState(0);
  const [parentLinkInfo, setParentLinkInfo] = useState<any>(null);
  const [expire, setExpire] = useState<any>(false);
  const [formValue, setFormValue] = useState<any>({});
  const [loading, setLoading] = useState<boolean>(false);
  const ref = useRef(true);
  const navigate = useNavigate();

  const getSchools = (franchiseeId: any) => {
    dispatch(parentLinkAction({ franchiseeId, apiAction: "schools" }))
      .then((result: any) => {
        if (result?.payload?.status === 200) {
          setSchoolOption(
            CommonUtil.formateDropdownList(
              result?.payload?.data?.data,
              "schoolName"
            )
          );
        } else {
          Toaster.error(undefined, result.payload);
          setExpire(true);
        }
      })
      .catch((err: any) => {
        // This console for debug the app if any error occurred in DEV
        console.log(err);
      });
  };

  const getStudentParentLinkInfo = () => {
    dispatch(parentLinkAction({ detailedId, apiAction: "parent-link-info" }))
      .then((result: any) => {
        if (result?.payload?.status === 200) {
          getSchools(result?.payload?.data?.data?.franchiseeId);
          setParentLinkInfo(result?.payload?.data.data);
        } else {
          Toaster.error(undefined, result.payload);
          setExpire(true);
        }
      })
      .catch((err: any) => {
        // This console for debug the app if any error occurred in DEV
        console.log(err);
      });
  };

  // ------------- Parent Info Validation -----------
  const parentInfoInitialValue = {
    parentName: "",
    primaryMobileNumber: parentLinkInfo?.primaryMobileNumber || "",
    whatsAppMobileNumber: parentLinkInfo?.primaryMobileNumber || "",
    email: parentLinkInfo?.email || "",
    country: countryOption.find(
      (item: any) => item.label?.toLowerCase() === "india"
    )?.label,
    countryId: countryOption.find(
      (item: any) => item.label?.toLowerCase() === "india"
    )?.value,
    pinCode: "",
    state: "",
    city: "",
    area: "",
    address: "",
    sameAsAbove: true,
  };
  const parentInfoValidation = Yup.object().shape({
    parentName: Yup.string()
      .required("Primary Parent/Guardian Name is required")
      .matches(
        /^[A-Za-z][A-Za-z\s.]*$/,
        `${t("Primary Parent/Guardian Name")} ${t("is")} ${t("incorrect")}`
      ),
    primaryMobileNumber: Yup.string()
      .min(10, `${t("primaryMobileNumber")} ${t("is")} ${t("incorrect")}`)
      .required(`${t("primaryMobileNumber")} ${t("is")} ${t("required")}`)
      .matches(
        FormValidation.getMobileNumberPattern(),
        `${t("primaryMobileNumber")} ${t("is")} ${t("incorrect")}`
      ),
    whatsAppMobileNumber: Yup.string()
      .min(10, `${t("whatsAppNumber")} ${t("is")} ${t("incorrect")}`)
      .required(`${t("whatsAppNumber")} ${t("is")} ${t("required")}`)
      .matches(
        FormValidation.getMobileNumberPattern(),
        `${t("whatsAppNumber")} ${t("is")} ${t("incorrect")}`
      ),
    email: Yup.string().matches(
      FormValidation.getEmailPattern(),
      `${t("email")} ${t("is")} ${t("incorrect")}`
    ),
    pinCode: Yup.number()
      .required("Pincode is required")
      .typeError("Pin Code must be a number")
      .min(100000, "Pin Code must be exactly 6 digits")
      .max(999999, "Pin Code must be exactly 6 digits"),
    country: Yup.string()
      .required("Country is required")
      .matches(
        FormValidation.getNamePattern(),
        `${t("country")} ${t("is")} ${t("incorrect")}`
      ),
  });

  // -------------- Student Info Validation ---------------
  const studentInfoInitialValue = {
    studentName: "",
    dob: "",
    school: "",
    gender: "",
  };
  const studentInfoValidation = Yup.object().shape({
    studentName: Yup.string()
      .required("Student Name is required")
      .matches(
        /^[A-Za-z][A-Za-z\s.]*$/,
        `${t("Student Name")} ${t("is")} ${t("incorrect")}`
      ),
    gender: Yup.string().required("Gender is required"),
    dob: Yup.string().required("DOB is required"),
  });

  // -------------- Invoice Payment Validation ---------------
  const invoicePaymentInitialValue = {
    invoiceAmount: parentLinkInfo?.invoiceAmount || 0,
    discountAmount: parentLinkInfo?.discountAmount || 0,
    totalAmountToBePaid: parentLinkInfo?.totalAmountToBePaid || 0,
    courseInstructorId: parentLinkInfo?.courseInstructorId || "",
  };
  const invoicePaymentValidation = Yup.object().shape({
    /* nothting  */
  });

  const steps = [
    {
      label: "Parent Info ",
      validation: parentInfoValidation,
      initialValue: parentInfoInitialValue,
    },
    {
      label: "Student Info",
      validation: studentInfoValidation,
      initialValue: studentInfoInitialValue,
    },
    {
      label: "Invoice & Payment",
      validation: invoicePaymentValidation,
      initialValue: invoicePaymentInitialValue,
    },
  ];

  const handleNext = (values: any) => {
    setFormValue((prevValues: any) => ({
      ...prevValues,
      [activeStep]: values,
    }));
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleCancel = () => {
    setActiveStep(0);
    window.open("about:blank", "_self");
    window.close();
  };

  const payNow = (values: any) => {
    setLoading(true);
    let payload: any = apiPreProcess(values);
    payload.payNow = true;

    dispatch(
      parentLinkAction({
        formValues: payload,
        apiAction: "addStudentDetails",
      })
    )
      .then((result: any) => {
        if (result?.payload?.status === 200) {
          Toaster.success(undefined, result?.payload?.data?.data?.message);
          const newPageUrl = result?.payload?.data?.data?.paymentLink;
          window.open(newPageUrl, "_self"); // to open page
        } else {
          Toaster.error(undefined, result?.payload?.data?.errors);
          setLoading(false);
        }
      })
      .catch((err: any) => {
        // This console for debugging the app if any error occurred in DEV
        console.log(err);
        setLoading(false);
      });
  };

  const apiPreProcess = (values: any) => {
    let updateValue = {
      ...formValue,
      [activeStep]: values,
    };
    setFormValue(updateValue);
    let payload: any = {};

    Object.keys(updateValue).forEach((key) => {
      Object.assign(payload, updateValue[key]);
    });
    payload.dob = moment(payload?.dob?.$d).format("DD/MM/YYYY");
    payload.generateParentLinkId = detailedId;
    payload.discountIfAny = parentLinkInfo.discountAmount;
    payload.courseInstructorId = parentLinkInfo.courseInstructorId;
    payload.franchiseeId = parentLinkInfo.franchiseeId;
    payload.totalAmount = values.totalAmountToBePaid;
    payload.courseId = parentLinkInfo.courseId;
    payload.levelOfAdmissionId = parentLinkInfo.levelOfAdmissionId;
    payload.registrationTypeId = parentLinkInfo.registrationTypeId;
    payload.tuitionFeesType = parentLinkInfo.tuitionFeesType;
    payload.multipleMonthCount = parentLinkInfo.multipleMonthCount;
    payload.feeMonth = parentLinkInfo.feeMonth?.toString();
    payload.primaryBenefitYouExpect = parentLinkInfo.primaryBenefitYouExpect;
    payload.registrationLevelStartDate =
      parentLinkInfo.registrationLevelStartDate;
    payload.registrationLevelEndDate = parentLinkInfo.registrationLevelEndDate;
    delete payload.sameAsAbove;

    return payload;
  };

  const handleSubmit = (values: any, { resetForm }: any) => {
    setLoading(true);
    let payload: any = apiPreProcess(values);
    payload.payNow = false;

    dispatch(
      parentLinkAction({
        formValues: payload,
        apiAction: "addStudentDetails",
      })
    )
      .then((result: any) => {
        if (result?.payload?.status === 200) {
          Toaster.success(undefined, result?.payload?.data?.data?.message);
          navigate("/registered-success");
        } else {
          Toaster.error(undefined, result?.payload?.data?.errors);
          setLoading(false);
        }
      })
      .catch((err: any) => {
        // This console for debugging the app if any error occurred in DEV
        console.log(err);
        setLoading(false);
      });
  };

  const getOption = () => {
    dispatch(parentLinkAction({ apiAction: "Country" }))
      .then((result: any) => {
        if (result?.payload?.status === 200) {
          setCountryOption(
            CommonUtil.formateDropdownList(
              result?.payload?.data?.data,
              "countryDisplayName"
            )
          );
        } else {
          Toaster.error(undefined, result.payload);
        }
      })
      .catch((err: any) => {
        console.log(err); //This console for debug the app if any error occurred in DEV
      });
  };

  useEffect(() => {
    if (ref.current) {
      ref.current = false;
      if (detailedId) {
        getStudentParentLinkInfo();
        getOption();
      } else {
        Toaster.error(
          undefined,
          "Invalid link, Please contact your admistrator!"
        );
      }
    }
  }, [detailedId]);

  const formValues = formValue[activeStep] || steps[activeStep]?.initialValue;
  return (
    <>
      {!expire && detailedId ? (
        <Box paddingTop="20px" sx={{ width: "100%" }}>
          <Stepper
            alternativeLabel
            activeStep={activeStep}
            connector={<StepperConnector />}
          >
            {steps.map((label, i) => (
              <Step key={i}>
                <StepLabel StepIconComponent={ColorlibStepIcon}>
                  {label.label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>
          {activeStep === steps.length ? (
            ""
          ) : (
            <Formik
              initialValues={formValues}
              validationSchema={steps[activeStep]?.validation}
              enableReinitialize={true}
              validate={(values: any) => {
                let errors: any = {};

                if (values?.invoiceAmount < values?.totalAmountToBePaid) {
                  errors.totalAmountToBePaid = t(
                    "generateParentLinkError.amountPaidCannotBeMoreThenInvoiceAmount"
                  );
                }

                return errors;
              }}
              onSubmit={(values: any, { setSubmitting, resetForm }: any) =>
                handleSubmit(values, { setSubmitting, resetForm })
              }
            >
              {({
                values,
                handleChange,
                handleBlur,
                errors,
                touched,
                setFieldValue,
                dirty,
              }) => {
                return (
                  <Form>
                    <Typography sx={{ mt: 2, mb: 1 }}>
                      {activeStep === 0 ? (
                        <ParentLinkParentInfo
                          values={values}
                          countryOption={countryOption}
                          handleChange={handleChange}
                          handleBlur={handleBlur}
                          errors={errors}
                          touched={touched}
                          setFieldValue={setFieldValue}
                        />
                      ) : null}
                      {activeStep === 1 ? (
                        <ParentLinkStudentInfo
                          values={values}
                          handleChange={handleChange}
                          schoolOption={schoolOption}
                          handleBlur={handleBlur}
                          errors={errors}
                          touched={touched}
                          setFieldValue={setFieldValue}
                        />
                      ) : null}
                      {activeStep === 2 ? (
                        <ParentLinkRegistrationPayment
                          values={values}
                          parentLinkInfo={parentLinkInfo}
                          handleChange={handleChange}
                          handleBlur={handleBlur}
                          errors={errors}
                          touched={touched}
                          setFieldValue={setFieldValue}
                        />
                      ) : null}
                    </Typography>
                    <Box
                      className="border-top padding-up-15 center-end flex-gap"
                      sx={{
                        flexDirection: "row",
                        pt: 2,
                        justifyContent: "end",
                      }}
                    >
                      {activeStep === 0 ? (
                        <Button sx={{ mr: 1 }} onClick={handleCancel}>
                          <ActionButton
                            label={"cancel"}
                            type="button"
                            actionButtonStyle="outline"
                            disabled={activeStep === 0}
                            onClick={handleCancel}
                          ></ActionButton>
                        </Button>
                      ) : (
                        <Button sx={{ mr: 1 }}>
                          <ActionButton
                            onClick={handleBack}
                            type="button"
                            label={"Previous"}
                            actionButtonStyle="outline"
                          ></ActionButton>
                        </Button>
                      )}
                      {activeStep === steps.length - 1 && (
                        <Button>
                          <ActionButton
                            loading={loading}
                            disabled={
                              activeStep === 0 || Object.keys(errors).length > 0
                            }
                            label={"Submit"}
                          ></ActionButton>
                        </Button>
                      )}
                      {activeStep === steps.length - 1 &&
                        parentLinkInfo?.showPayNow && (
                          <Button>
                            <ActionButton
                              onClick={() => payNow(values)}
                              disabled={
                                activeStep === 0 ||
                                Object.keys(errors).length > 0
                              }
                              type="button"
                              label={"Pay Now"}
                            ></ActionButton>
                          </Button>
                        )}
                      {activeStep !== steps.length - 1 && (
                        <Button type="button">
                          <ActionButton
                            onClick={() => handleNext(values)}
                            type="button"
                            disabled={
                              (activeStep === 0 &&
                                !formValue[activeStep] &&
                                Object.keys(touched).length === 0) ||
                              Object.keys(errors).length > 0 ||
                              (!formValue[activeStep] && !dirty)
                            }
                            label={"Next"}
                          />
                        </Button>
                      )}
                    </Box>
                  </Form>
                );
              }}
            </Formik>
          )}
        </Box>
      ) : (
        <img
          src={LinkExpire}
          style={{ height: "100%", width: "100%" }}
          alt="Expire"
        />
      )}
    </>
  );
};

export default ParentLinkStepperContainer;
